import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Company, CreateCompanyData, UpdateCompanyData } from '../../types/organization.types';
import { useCreateCompany, useUpdateCompany } from '../../hooks/useOrgQueries';
import { LedgerDialog, LedgerField, LedgerInput } from '../ui';
import { cn } from '../../lib/cn';

// Optional fields carry .defined().default(...) so the schema's inferred type
// lines up with what yupResolver hands back — a bare .string() infers as an
// optional property on one side but required-with-undefined on the other.
const schema = yup.object({
  name: yup
    .string()
    .required('Company name is required')
    .test('non-blank', 'Company name is required', (value) => Boolean(value && value.trim())),
  description: yup.string().defined().default(''),
  website: yup.string().url('Enter a valid URL').defined().default(''),
  email: yup.string().email('Enter a valid email').defined().default(''),
  phone: yup.string().defined().default(''),
  address: yup.string().defined().default(''),
  maxTeams: yup
    .number()
    .typeError('Must be a number')
    .min(1, 'Must be at least 1')
    .nullable()
    .defined()
    .default(10),
  maxUsers: yup
    .number()
    .typeError('Must be a number')
    .min(1, 'Must be at least 1')
    .nullable()
    .defined()
    .default(100),
  isActive: yup.boolean().defined().default(true),
});

type CompanyFormValues = yup.InferType<typeof schema>;

const defaultValues: CompanyFormValues = {
  name: '',
  description: '',
  website: '',
  email: '',
  phone: '',
  address: '',
  maxTeams: 10,
  maxUsers: 100,
  isActive: true,
};

interface CompanyFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  company?: Company | null;
}

export const CompanyFormDialog: React.FC<CompanyFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  company,
}) => {
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const saving = createCompany.isPending || updateCompany.isPending;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CompanyFormValues>({ resolver: yupResolver(schema), defaultValues, mode: 'onTouched' });

  // Reseed whenever the dialog opens or its subject changes so a stale edit
  // subject or error strip never leaks into the next open.
  useEffect(() => {
    setError(null);
    if (company) {
      reset({
        name: company.name,
        description: company.description || '',
        website: company.website || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        maxTeams: company.maxTeams,
        maxUsers: company.maxUsers,
        isActive: company.isActive,
      });
    } else {
      reset(defaultValues);
    }
  }, [company, open, reset]);

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    onClose();
  };

  const onSubmit = async (values: CompanyFormValues) => {
    setError(null);
    try {
      if (company) {
        const data: UpdateCompanyData = {
          name: values.name,
          description: values.description,
          website: values.website,
          email: values.email,
          phone: values.phone,
          address: values.address,
          maxTeams: values.maxTeams ?? undefined,
          maxUsers: values.maxUsers ?? undefined,
          isActive: values.isActive,
        };
        await updateCompany.mutateAsync({ id: company.id, data });
      } else {
        const data: CreateCompanyData = {
          name: values.name,
          description: values.description,
          website: values.website,
          email: values.email,
          phone: values.phone,
          address: values.address,
          maxTeams: values.maxTeams ?? undefined,
          maxUsers: values.maxUsers ?? undefined,
        };
        await createCompany.mutateAsync(data);
      }
      onSuccess();
      handleClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || 'Failed to save company');
    }
  };

  const nameField = register('name');

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={company ? 'Edit Company' : 'Create New Company'}
      size="md"
      preventClose={saving}
      initialFocus={nameInputRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="company-form"
            disabled={saving || isSubmitting}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {saving ? 'Saving…' : company ? 'Update' : 'Create'}
          </button>
        </>
      }
    >
      <form id="company-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {error && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error}
          </p>
        )}
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <LedgerField
            label="Company Name"
            htmlFor="company-name"
            error={errors.name?.message}
            className="sm:col-span-2"
          >
            <LedgerInput
              id="company-name"
              required
              invalid={!!errors.name}
              {...nameField}
              ref={(element) => {
                nameField.ref(element);
                nameInputRef.current = element;
              }}
            />
          </LedgerField>

          <LedgerField label="Description" htmlFor="company-description" className="sm:col-span-2">
            <textarea
              id="company-description"
              rows={3}
              className="w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent"
              {...register('description')}
            />
          </LedgerField>

          <LedgerField label="Website" htmlFor="company-website" error={errors.website?.message}>
            <LedgerInput id="company-website" invalid={!!errors.website} {...register('website')} />
          </LedgerField>

          <LedgerField label="Email" htmlFor="company-email" error={errors.email?.message}>
            <LedgerInput id="company-email" type="email" invalid={!!errors.email} {...register('email')} />
          </LedgerField>

          <LedgerField label="Phone" htmlFor="company-phone">
            <LedgerInput id="company-phone" {...register('phone')} />
          </LedgerField>

          <LedgerField label="Address" htmlFor="company-address">
            <LedgerInput id="company-address" {...register('address')} />
          </LedgerField>

          <div className="cap-label mb-3 mt-1 border-b border-ink pb-0.5 sm:col-span-2">Limits</div>

          <LedgerField label="Maximum Teams" htmlFor="company-max-teams" error={errors.maxTeams?.message}>
            <LedgerInput
              id="company-max-teams"
              type="number"
              invalid={!!errors.maxTeams}
              {...register('maxTeams', { valueAsNumber: true })}
            />
          </LedgerField>

          <LedgerField label="Maximum Users" htmlFor="company-max-users" error={errors.maxUsers?.message}>
            <LedgerInput
              id="company-max-users"
              type="number"
              invalid={!!errors.maxUsers}
              {...register('maxUsers', { valueAsNumber: true })}
            />
          </LedgerField>

          {company && (
            <div className="sm:col-span-2">
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={field.value}
                    aria-label="Active"
                    onClick={() => field.onChange(!field.value)}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <span aria-hidden className={cn('h-3 w-3 border border-ink', field.value && 'bg-ink')} />
                    <span className="cap-label">{field.value ? 'Active' : 'Not active'}</span>
                  </button>
                )}
              />
            </div>
          )}
        </div>
      </form>
    </LedgerDialog>
  );
};
